import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Check, Sparkles, ArrowRight } from 'lucide-react';

const features = [
  'AI-Powered SOAP Notes',
  'Smart Scheduling & Reminders',
  'Native Telehealth (Video)',
  'Claims & Billing Automation',
  'Client Portal Access',
  'Clinical Assessments (PHQ-9, GAD-7)',
  'Real-Time Analytics Dashboard',
  'EHR Integration',
  'HIPAA-Compliant Platform',
  'Dedicated Support',
];

const plans = [
  {
    name: 'Monthly',
    price: 199,
    period: '/month',
    billing: 'per user, billed monthly',
    description: 'Flexible month-to-month access to the full Doctorite platform.',
    highlighted: false,
    badge: null,
  },
  {
    name: 'Annual',
    price: 149,
    period: '/month',
    billing: 'per user, billed annually',
    description: 'Best value — commit annually and save on every seat.',
    highlighted: true,
    badge: 'Save 25%',
  },
];

export default function Pricing() {
  const { ref, inView } = useScrollAnimation();

  return (
    <section id="pricing" className="section-padding bg-gradient-to-b from-gray-50 to-white" ref={ref}>
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-sm font-medium text-brand-700 mb-4">
            <Sparkles className="w-4 h-4" />
            Simple, Transparent Pricing
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            One Platform.{' '}
            <span className="gradient-text">One Price.</span>
          </h2>
          <p className="text-lg text-gray-600">
            Every plan includes full access to all features — AI documentation, telehealth, billing, scheduling, and more. No hidden fees. No feature gates.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className={`relative rounded-2xl p-8 sm:p-10 transition-all duration-300 ${
                plan.highlighted
                  ? 'bg-white border-2 border-brand-500 shadow-xl shadow-brand-900/10'
                  : 'bg-white border border-gray-200 shadow-lg shadow-brand-900/5'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center px-4 py-1.5 bg-accent-500 text-white text-xs font-bold rounded-full uppercase tracking-wide">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-sm text-gray-500 mb-6">{plan.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-sm font-medium text-gray-500">$</span>
                  <span className={`text-5xl font-extrabold tracking-tight ${plan.highlighted ? 'text-brand-600' : 'text-gray-900'}`}>
                    {plan.price}
                  </span>
                  <span className="text-base font-medium text-gray-500">{plan.period}</span>
                </div>
                <p className="text-sm text-gray-400 mt-1">{plan.billing}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 shrink-0 mt-0.5 ${plan.highlighted ? 'text-brand-500' : 'text-accent-500'}`} />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/register"
                className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold rounded-xl transition-all duration-200 group ${
                  plan.highlighted
                    ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-lg shadow-brand-600/25'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                Start Free Trial
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center text-sm text-gray-400 mt-8"
        >
          All plans include a 7-day free trial. No credit card required.
        </motion.p>
      </div>
    </section>
  );
}
